import { create } from 'zustand';
import { MAX_LEVEL_SCALE } from './engines/levelChoice';

/**
 * Speed or sharpness: the pixels the viewer chooses a slide's pyramid level
 * by (levelChoice.scaleLevelChoice). Speed, the default, chooses per layout
 * (CSS) pixel: on a dense screen a level pixel is drawn across several screen
 * pixels, and a screen is the fewest tiles. Sharpness chooses per screen
 * pixel, so a level pixel lands on about one screen pixel: on a Retina
 * display four times the tiles a screen, the same again in bytes. On a
 * screen of one pixel per layout pixel the two are the same.
 *
 * Sharpness chooses per screen pixel once the view is still (stillSharp),
 * and per layout pixel while it pans and zooms, as speed does, unless
 * Sharpness in Motion is on (the user, 2026-10-12): the four times the tiles
 * go only to what is looked at, unless asked for while moving too.
 *
 * Speed or Sharpness is in the viewer's bottom bar (ViewerBottomBar), in
 * community's Settings and on enterprise's profile page; Sharpness in Motion
 * in those two only, shown under Sharpness. Both are kept in this browser and
 * on the account (`viewerSharpness`, `viewerSharpMotion`), and a change has
 * every pane choose its level again at once (DmvEngine rechooseLevels: back
 * to the lighter level, sharp again STILL_MS later where it should be).
 */
const KEY = 'tr.viewerSharpness';

function stored(): boolean {
  try {
    return window.localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

/** Whether the pane chooses levels per screen pixel now: sharpness, while still unless Sharpness in Motion. */
export function choosesSharp(o: { sharpness: boolean; sharpMotion: boolean; still: boolean }): boolean {
  return o.sharpness && (o.sharpMotion || o.still);
}

const MOTION_KEY = 'tr.viewerSharpMotion';

function storedMotion(): boolean {
  try {
    return window.localStorage.getItem(MOTION_KEY) === '1';
  } catch {
    return false;
  }
}

/** Sharpness in Motion: sharpness's level while the view moves too. Off unless chosen. */
export const useViewerSharpMotion = create<{ on: boolean; set: (on: boolean) => void }>((set, get) => ({
  on: storedMotion(),
  // The same choice again changes nothing: its subscribers (DmvEngine) would re-choose every pane's level.
  set: (on) => {
    if (on === get().on) return;
    try {
      window.localStorage.setItem(MOTION_KEY, on ? '1' : '0');
    } catch {
      /* private browsing: the choice lasts this page */
    }
    set({ on });
  },
}));

export const useViewerSharpness = create<{ sharp: boolean; set: (sharp: boolean) => void }>((set, get) => ({
  sharp: stored(),
  // The same choice again changes nothing (as useViewerSharpMotion).
  set: (sharp) => {
    if (sharp === get().sharp) return;
    try {
      window.localStorage.setItem(KEY, sharp ? '1' : '0');
    } catch {
      /* private browsing: the choice lasts this page */
    }
    set({ sharp });
  },
}));

/**
 * Device pixels per CSS pixel to choose levels by: 1 for speed; for
 * sharpness the screen's density, `density` (as OpenLayers renders it), at
 * most MAX_LEVEL_SCALE and never below 1.
 */
export function wantedLevelScale(sharp: boolean, density: number): number {
  return sharp ? Math.min(MAX_LEVEL_SCALE, Math.max(1, density || 1)) : 1;
}
